import { enumerateBuckets, periodBucket } from '@timelog/pm-shared';
import type { UtilisationGranularity } from '@timelog/pm-shared';
import type { TFunction } from 'i18next';

import { isoToEpochDay } from '@/lib/epochDay';

/**
 * The planner's time window and period-bucket helpers. Pure; all date maths
 * is UTC epoch-day arithmetic (never a local Date). Bucket KEYS come from the
 * shared `periodBucket`/`enumerateBuckets` so the client and the API can
 * never disagree on where a week starts.
 */

const MONTHS_BEFORE = 1;
const MONTHS_AFTER = 11;

/** Keys under `pmResources:period.month` — the English short month names. */
const MONTH_KEYS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

const utcDate = (iso: string): Date => new Date(`${iso}T00:00:00Z`);

/** Last month + the next eleven — the same window at every granularity. */
export const plannerRange = (todayIso: string): { from: string; to: string } => {
  const today = utcDate(todayIso);
  const from = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() - MONTHS_BEFORE, 1));
  const to = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() + MONTHS_AFTER + 1, 0));
  return { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) };
};

export const plannerBuckets = (
  range: { from: string; to: string },
  granularity: UtilisationGranularity,
): string[] => enumerateBuckets(range.from, range.to, granularity);

/** ISO-8601 week number of a week-bucket key (the week's Monday). */
const isoWeekNumber = (mondayIso: string): number => {
  const monday = utcDate(mondayIso);
  const thursday = new Date(monday.getTime() + 3 * 86_400_000);
  const january4 = Date.UTC(thursday.getUTCFullYear(), 0, 4);
  const january4Weekday = (new Date(january4).getUTCDay() + 6) % 7;
  const week1Monday = january4 - january4Weekday * 86_400_000;
  return 1 + Math.round((monday.getTime() - week1Monday) / (7 * 86_400_000));
};

export type BucketLabel = { label: string; sub: string };

/** The `t` the planner's pure modules take: its own namespace plus the shared glossary. */
export type PlannerT = TFunction<['pmResources', 'pmCommon']>;

const monthLabel = (t: PlannerT, monthIndex: number): string | undefined => {
  const key = MONTH_KEYS[monthIndex];
  return key === undefined ? undefined : t(`period.month.${key}`);
};

/** Header caption for a bucket key ('2026-06-01' | '2026-06' | '2026-Q2'). */
export const bucketLabel = (
  bucket: string,
  granularity: UtilisationGranularity,
  t: PlannerT,
): BucketLabel => {
  if (granularity === 'week') {
    const monday = utcDate(bucket);
    return {
      label: t('period.WeekLabel', { week: String(isoWeekNumber(bucket)) }),
      sub: t('period.WeekSub', {
        day: String(monday.getUTCDate()),
        month: monthLabel(t, monday.getUTCMonth())!,
      }),
    };
  }
  if (granularity === 'month') {
    const monthIndex = Number(bucket.slice(5, 7)) - 1;
    return { label: monthLabel(t, monthIndex) ?? bucket, sub: `’${bucket.slice(2, 4)}` };
  }
  if (granularity === 'quarter') {
    return {
      label: t('period.QuarterLabel', { quarter: bucket.slice(6) }),
      sub: `’${bucket.slice(2, 4)}`,
    };
  }
  const day = utcDate(bucket);
  return {
    label: String(day.getUTCDate()),
    sub: monthLabel(t, day.getUTCMonth())!,
  };
};

/** Inclusive day count of the bucket today falls in. */
const bucketDayCount = (bucket: string, granularity: UtilisationGranularity): number => {
  if (granularity === 'day') return 1;
  if (granularity === 'week') return 7;
  if (granularity === 'month') {
    const year = Number(bucket.slice(0, 4));
    const month = Number(bucket.slice(5, 7));
    return new Date(Date.UTC(year, month, 0)).getUTCDate();
  }
  const year = Number(bucket.slice(0, 4));
  const quarter = Number(bucket.slice(6));
  const start = Date.UTC(year, (quarter - 1) * 3, 1);
  const end = Date.UTC(year, quarter * 3, 1);
  return Math.round((end - start) / 86_400_000);
};

/** Epoch day of the first day of a bucket. */
const bucketStartDay = (bucket: string, granularity: UtilisationGranularity): number | null => {
  if (granularity === 'day' || granularity === 'week') return isoToEpochDay(bucket);
  if (granularity === 'month') return isoToEpochDay(`${bucket}-01`);
  const year = bucket.slice(0, 4);
  const quarter = Number(bucket.slice(6));
  const month = String((quarter - 1) * 3 + 1).padStart(2, '0');
  return isoToEpochDay(`${year}-${month}-01`);
};

/**
 * X position of the today line inside the track, or null when today falls
 * outside the visible buckets: the containing bucket's left edge plus the
 * day fraction into it.
 */
export const todayLineX = (
  todayIso: string,
  buckets: readonly string[],
  granularity: UtilisationGranularity,
  cellWidthPx: number,
): number | null => {
  const bucket = periodBucket(todayIso, granularity);
  const index = buckets.indexOf(bucket);
  if (index === -1) return null;
  const today = isoToEpochDay(todayIso);
  const start = bucketStartDay(bucket, granularity);
  if (today === null || start === null) return index * cellWidthPx;
  const fraction = (today - start) / bucketDayCount(bucket, granularity);
  return index * cellWidthPx + fraction * cellWidthPx;
};
